import algoliasearch from 'algoliasearch/lite'
import appSync from '@/_services/appsync.service.js'
import { vehicleService } from '@/_services'
import { shipmentHelper } from '@/_helpers'
import gql from 'graphql-tag'

export const shipmentService = {
  getShipmentsForNextDays,
  getShipmentSummaryTable,
  getShipmentsForDay,
  getNextDaysArray,
  getDropInShipment,
  getShipment,
  getShipmentWithDrops,
  getShipmentWithDropsForOptimization,
  getShipmentWithDropsTransport,
  getShipmentSchedule,
  getBatchShipmentDrops,
  getShipmentWithDropsUnitsCount,
  getShipmentForContext,
  createShipment,
  updateShipmentLabels,
  getSchedule,
  setGoodsValue
}

function getNextDaysArray (numberOfDays) {
  let days = []
  for (let i = 0; i < numberOfDays; i++) {
    let date = new Date()
    date.setDate(date.getDate() + i)

    const dateISO = date.getFullYear() + '-' +
      (date.getMonth() + 1 < 10 ? '0' : '') + (date.getMonth() + 1) + '-' +
      (date.getDate() < 10 ? '0' : '') + date.getDate()

    days.push(dateISO)
  }
  return days
}

/**
 * Get shipments for next days from Algolia
 * @param {Number} numberOfDays
 */
async function getShipmentsForNextDays (numberOfDays = 10) {
  let shipments = {}
  const dates = getNextDaysArray(numberOfDays)
  for (let i = 0; i < dates.length; i++) {
    const date = dates[i]
    shipments[date] = await getShipmentsForDay(date)
  }

  return shipments
}

function getShipmentSummaryTable (shipmentsByDays) {
  let summaryByDays = {}
  Object.keys(shipmentsByDays).forEach(index => {
    const shipmentInDay = shipmentsByDays[index] === undefined ? [] : shipmentsByDays[index]

    let totalShipments = shipmentInDay.length
    let totalVolume = 0
    let totalWeight = 0
    let totalPackages = 0
    let status = ''

    for (let i = 0; i < totalShipments; i++) {
      totalVolume += shipmentInDay[i].volume
      totalWeight += shipmentInDay[i].weight
      // totalPackages += shipmentInDay[i].units
      Object.keys(shipmentInDay[i].units).forEach(type => {
        totalPackages += shipmentInDay[i].units[type].count
      })

      status = shipmentInDay[i].status
    }

    summaryByDays[index] = {
      shipments: totalShipments,
      volume: totalVolume,
      weight: totalWeight,
      packages: totalPackages,
      status: status
    }
  })

  return summaryByDays
}

/**
 *
 * @param {String} day - date in ISO format (e.g. 2019-10-30)
 */
async function getShipmentsForDay (day) {
  let date = new Date(day)
  date.setHours(0, 0, 0, 0) // remove time
  date = Math.floor(date.getTime() / 1000) // date to timestamp in seconds
  // console.log('date service', date)
  const ALGOLIA_ATRIBUTE_FOR_DATE = 'arrival'
  const DAY_IN_SECONDS = 86400
  const ALGOLIA_INDEX = 'prod_shipments'
  const ALGOLIA_CLIENT = algoliasearch('9N5LGLZPRT', 'e5b8a01d8c6ca064424f71e630360d15')
  const algoliaIndex = ALGOLIA_CLIENT.initIndex(ALGOLIA_INDEX)
  const oneDayFilter = ALGOLIA_ATRIBUTE_FOR_DATE + ':' + date + ' TO ' + (date + DAY_IN_SECONDS - 1)

  // console.log('oneDayFilter', oneDayFilter)
  try {
    const searchResult = await algoliaIndex.search({
      hitsPerPage: 1000,
      filters: oneDayFilter
    })
    // console.log('searchResult', searchResult)
    return searchResult.hits
  } catch (error) {
    console.log(error)
    return []
  }
}

async function getBatchShipmentDrops ({ dropIDs }) {
  return Promise.all(dropIDs.map(async (id, index) => {
    const response = await getDropInShipment({ id })
    return response.data.getDrop.drop
  }))
}

// Queries
const GET_DROP = gql`query getDrop ($input: GetDropInput!) {
  getDrop (input: $input) {
    drop {
      id
      destination {
        id
        address {
          street
          streetNo
          city
          country
          countryCode
          postalCode
        }
        ref
        organization {
          id
          name
          ref
        }
      }
      origin {
        id
        address {
          street
          streetNo
          city
          country
          countryCode
          postalCode
        }
        ref
        organization {
          id
          name
          ref
        }
      }
      departure
      arrival
      weight
      volume
      shipment
      unitsCount {
        type
        count
      }
      status
    }
  }
}`
const GET_SHIPMENT = gql`query getShipment($input: GetShipmentInput!) {
  getShipment(input: $input){
    shipment
    {
        id
        type
        labels
        status
        drops {
          id
          destination {
            id
            organization {
              id
              name
            }
            address {
              street
              streetNo
              city
              country
              countryCode
              postalCode
            }
          }
          departure
          arrival
          weight
          volume
        }
        units {
          type
          count
        }
        quotes {
          quoteID
          price
          currency
          status
          organization {
            name
          }
          originPort
          originAddress
          originCoordinates {
            lat
            lng
          }
          destinationPort
          destinationAddress
          destinationCoordinates {
            lat
            lng
          }
          transportType
        }
        carrier
        vehicle
        weight
        volume
        createdAt
        updatedAt,
        goodsValue {
          price,
          currency
        }
    }
  }
}`
const GET_SHIPMENT_SCHEDULE = gql`query getShipment($input: GetShipmentInput!) {
  getShipment(input: $input){
    shipment
    {
        id
        type
        labels
        status
        units {
          type
          count
        }
        drops {
          pickup {
            from
            to
          }
          delivery {
            from
            to
          }
        }
        schedule {
          stops {
            stopIndex
            type
            location {
              id
              name
              ref
              equipment
              accessibility
              address {
                street
                streetNo
                city
                country
                countryCode
                postalCode
              }
              organization {
                id
                name
                ref
              }
              coordinates {
                lat
                lng
              }
            }
            arrival
            departure
            timeSpent
          }
          connections {
            stopIndexFrom
            stopIndexTo
            distance
            duration
          }
          metrics {
            totalDistance
            totalDuration
            numberOfLocations
            numberOfOtherStops
          }
        }
        carrier
        vehicle
        weight
        volume
        createdAt
        updatedAt
    }
  }
}`
const GET_SHIPMENT_WITH_DROPS = gql`query getShipment($input: GetShipmentInput!) {
  getShipment(input: $input){
    shipment
    {
        id
        type
        labels
        drops {
          id
          origin {
            id
            name
            ref
            address {
              street
              streetNo
              city
              country
              countryCode
              postalCode
            }
            coordinates {
              lat
              lng
            }
            organization {
              id
              ref
              name
              regNo
              vatNo
            }
            equipment
            accessibility
          }
          destination {
            id
            name
            ref
            address {
              street
              streetNo
              city
              country
              countryCode
              postalCode
            }
            coordinates {
              lat
              lng
            }
            organization {
              id
              ref
              name
              regNo
              vatNo
            }
            equipment
            accessibility
          }
          unitsCount {
            type
            count
          }
          departure
          arrival
          weight
          volume
          status
        }
        status
        units {
          type
          count
        }
        carrier
        vehicle
        weight
        volume
        createdAt
        updatedAt
    }
  }
}`
const GET_SHIPMENT_WITH_DROPS_TRANSPORT = gql`query getShipment($input: GetShipmentInput!) {
  getShipment(input: $input){
    shipment
    {
        id
        type
        labels
        drops {
          id
          origin {
            id
            name
            ref
            address {
              street
              streetNo
              city
              country
              countryCode
              postalCode
            }
            coordinates {
              lat
              lng
            }
            organization {
              id
              ref
              name
              regNo
              vatNo
            }
            equipment
            accessibility
          }
          destination {
            id
            name
            ref
            address {
              street
              streetNo
              city
              country
              countryCode
              postalCode
            }
            coordinates {
              lat
              lng
            }
            organization {
              id
              ref
              name
              regNo
              vatNo
            }
            equipment
            accessibility
          }
          transports {
            id
            ref
            priority
            units {
            id
            ref
            type
            parent
            weight
            packagingWeight
            isLeaf
            status
            dimensions {
              length
              height
              width
            },
            items {
              ref
              category
              name
              weight
            },
          }
            order {
              buyer {
                organization {
                  id
                  ref
                  name
                }
                ref
              }
              seller {
                organization {
                  id
                  ref
                  name
                }
                ref
              }
            }
          }
          # units {
          #   id
          #   ref
          #   type
          #   parent
          #   weight
          #   packagingWeight
          #   isLeaf
          #   status
          #   dimensions {
          #     length
          #     height
          #     width
          #   },
          #   items {
          #     ref
          #     category
          #     name
          #     weight
          #   },
          # }
          departure
          arrival
          weight
          volume
          status
        }
        status
        units {
          type
          count
        }
        carrier
        vehicle
        weight
        volume
        createdAt
        updatedAt
    }
  }
}`
const GET_SHIPMENT_WITHOUT_ORGANIZATION = gql`query getShipment($input: GetShipmentInput!) {
  getShipment(input: $input){
    shipment
    {
        id
        type
        labels
        drops {
          id
          origin {
            id
            name
            ref
            address {
              street
              streetNo
              city
              country
              countryCode
              postalCode
            }
            coordinates {
              lat
              lng
            }
            organization {
              id
              name
              regNo
              vatNo
            }
            equipment
            accessibility
          }
          destination {
            id
            name
            ref
            address {
              street
              streetNo
              city
              country
              countryCode
              postalCode
            }
            coordinates {
              lat
              lng
            }
            organization {
              id
              name
              regNo
              vatNo
            }
            equipment
            accessibility
          }
          departure
          arrival
          weight
          volume
        }
        status
        units {
          type
          count
        }
        carrier
        vehicle
        weight
        volume
        createdAt
        updatedAt
    }
  }
}`
const GET_SCHEDULE = gql`query getSchedule($input: GetScheduleInput!) {
  getSchedule(input: $input) {
    schedule {
      stops {
        stopIndex
        type
        location {
          id
        }
        arrival
        departure
        timeSpent
      }
      connections {
        stopIndexFrom
        stopIndexTo
        distance
        duration
      }
      metrics {
        totalDistance
        totalDuration
        numberOfLocations
        numberOfOtherStops
      }
    }
  }
}`
const GET_SHIPMENT_DROPS_UNITS_COUNT = gql`query getShipment($input: GetShipmentInput!) {
  getShipment(input: $input){
    shipment
    {
        id
        drops {
          id
          destination {
            id
            organization {
              id
              name
            }
            address {
              street
              streetNo
              city
              country
              countryCode
              postalCode
            }
          }
          origin {
            id
          }
          unitsCount {
            type
            count
          }
        }
    }
  }
}`
const GET_SHIPMENT_FOR_CONTEXT = gql`query getShipment($input: GetShipmentInput!) {
  getShipment(input: $input){
    shipment
    {
        id
        type
        labels
        status
        units {
          type
          count
        }
        schedule {
          stops {
            stopIndex
            type
            location {
              id
              name
              ref
              equipment
              accessibility
              address {
                street
                streetNo
                city
                country
                countryCode
                postalCode
              }
              organization {
                id
                name
                ref
              }
              coordinates {
                lat
                lng
              }
            }
            arrival
            departure
            timeSpent
          }
          connections {
            stopIndexFrom
            stopIndexTo
            distance
            duration
          }
          metrics {
            totalDistance
            totalDuration
            numberOfLocations
            numberOfOtherStops
          }
          departure
        }
        quotes {
          quoteID
          price
          currency
          status
          organization {
            name
          }
          originPort
          originAddress
          originCoordinates {
            lat
            lng
          }
          destinationPort
          destinationAddress
          destinationCoordinates {
            lat
            lng
          }
          transportType
        }
        goodsValue {
          price,
          currency
        }
        drops {
          pickup {
            from
            to
          }
          delivery {
            from
            to
          }
        }
        vehicle
        carrier
        weight
        volume
        createdAt
        updatedAt
    }
  }
}`
const GET_SHIPMENT_FOR_CONTEXT_V2 = gql`query getShipmentV2($input: GetShipmentV2Input!) {
  getShipmentV2(input: $input) {
    shipment {
      ... on NotTypedShipment {
        id
        labels
        status
        createdAt
        updatedAt
        warnings
        type
        drops {
          weight
          volume
        }
        quoting {
          quotingStatus
          noOfRequests
          noOfQuotes
          selectedQuote {
            status
          }
        }
        # pricing {
        #   price
        #   currency
        # }
      }
      ... on Shipment1PL {
        id
        labels
        status
        createdAt
        updatedAt
        warnings
        type
        pickupLocal {
          from
          to
        }
        deliveryLocal {
          from
          to
        }
        departureUTC
        drops {
          weight
          volume
        }
        schedule {
          metrics {
            totalDistance
            totalDuration
            numberOfLocations
            numberOfOtherStops
          }
        }
        quoting {
          quotingStatus
          noOfRequests
          noOfQuotes
          selectedQuote {
            quoteID
            price
            currency
            status
          }
        }
        pricing {
          price
          currency
        }
        vehicle {
          # name
          model
          type
          plateNumber
          maxWeight
          maxVolume
          organization {
            id
            name
          }
          equipment
          labels
        }
        driver
      }
      ... on Shipment2PL {
        id
        labels
        status
        createdAt
        updatedAt
        warnings
        pickupLocal {
          from
          to
        }
        deliveryLocal {
          from
          to
        }
        departureUTC
        drops {
          weight
          volume
        }
        schedule {
          metrics {
            totalDistance
            totalDuration
            numberOfLocations
            numberOfOtherStops
          }
        }
        quoting {
          quotingStatus
          noOfRequests
          noOfQuotes
          selectedQuote {
            status
          }
        }
        pricing {
          price
          currency
        }
        vehicleClass {
          type
          organization {
            id
            name
          }
          equipment
        }
      }
      ... on Shipment3PL {
        id
        labels
        status
        createdAt
        updatedAt
        warnings
        type
        drops {
          weight
          volume
        }
        quoting {
          quotingStatus
          noOfRequests
          noOfQuotes
          selectedQuote {
            quoteID
            price
            currency
            status
            organization {
              id
              name
            }
          }
        }
        pricing {
          price
          currency
        },
      }
    }
  }
}`

const GET_SHIPMENT_WITH_DROP_FOR_OPTIMIZATION = gql`query getShipment($input: GetShipmentInput!) {
  getShipment(input: $input){
    shipment
    {
      id
      vehicle
      type
      drops {
      arrival
      departure
        id
        origin {
          id
        }
        destination {
          id
        },
        pickup {
          from
          to
        },
        delivery {
          from
          to
        }
      }
    }
  }
}`

async function getShipmentWithDropsTransport ({ shipmentID }) {
  try {
    const response = await appSync.query({
      query: GET_SHIPMENT_WITH_DROPS_TRANSPORT,
      variables: {
        input: {
          id: shipmentID
        }
      },
      fetchPolicy: 'no-cache'
    })
    return response.data.getShipment.shipment
  } catch (error) {
    return Promise.reject(error)
  }
}

async function getShipment ({ shipmentID }) {
  try {
    const response = await appSync.query({
      query: GET_SHIPMENT,
      variables: {
        input: {
          id: shipmentID
        }
      },
      fetchPolicy: 'no-cache'
    })
    return response.data.getShipment.shipment
  } catch (error) {
    return Promise.reject(error)
  }
}

async function getShipmentSchedule ({ shipmentID, quote }) {
  try {
    const response = await appSync.query({
      query: GET_SHIPMENT_SCHEDULE,
      variables: {
        input: {
          id: shipmentID
        }
      },
      fetchPolicy: 'no-cache'
    })
    let schedule = response.data.getShipment.shipment.schedule
    const drops = response.data.getShipment.shipment.drops
    schedule = shipmentHelper.addQuotePortToSchedule({ schedule, quote, drops })
    return schedule
  } catch (error) {
    return Promise.reject(error)
  }
}

function getShipmentWithDrops ({ shipmentID }) {
  return new Promise((resolve, reject) => {
    appSync.query({
      query: GET_SHIPMENT_WITH_DROPS,
      variables: {
        input: {
          id: shipmentID
        }
      },
      fetchPolicy: 'no-cache'
    }).then((response) => {
      resolve(response.data.getShipment.shipment)
    }).catch((error) => {
      console.error(error)
      return appSync.query({
        query: GET_SHIPMENT_WITHOUT_ORGANIZATION,
        variables: {
          input: {
            id: shipmentID
          }
        },
        fetchPolicy: 'no-cache'
      })
    }).then((response) => {
      response.data.getShipment.shipment.drops.forEach((drop) => {
        drop.destination['organization'] = {
          name: null
        }
        drop.origin['organization'] = {
          name: null
        }
      })
      resolve(response.data.getShipment.shipment)
    }).catch((error) => {
      reject(error)
    })
  })
}

async function getDropInShipment ({ id }) {
  try {
    const response = await appSync.query({
      query: GET_DROP,
      variables: {
        input: {
          id: id
        }
      },
      fetchPolicy: 'no-cache'
    })
    return response
  } catch (error) {
    return Promise.reject(error)
  }
}
async function getSchedule ({ optimize, visited, locationsIDs, drops, vehicle, depot }) {
  try {
    const response = await appSync.query({
      query: GET_SCHEDULE,
      variables: {
        input: {
          optimize,
          // locationsIDs,
          drops
        }
      },
      fetchPolicy: 'no-cache'
    })
    return response.data.getSchedule.schedule
  } catch (error) {
    return Promise.reject(error)
  }
}

async function getShipmentWithDropsUnitsCount ({ shipmentID }) {
  try {
    const response = await appSync.query({
      query: GET_SHIPMENT_DROPS_UNITS_COUNT,
      variables: {
        input: {
          id: shipmentID
        }
      },
      fetchPolicy: 'no-cache'
    })
    return response.data.getShipment.shipment.drops
  } catch (error) {
    return Promise.reject(error)
  }
}

async function getShipmentForContext ({ shipmentID }) {
  try {
    const response = await appSync.query({
      query: GET_SHIPMENT_FOR_CONTEXT,
      variables: {
        input: {
          id: shipmentID
        }
      },
      fetchPolicy: 'no-cache'
    })
    // Transport type Air or Ocean
    let quote = null
    let shipment = response.data.getShipment.shipment
    if (shipment.type === 'ThreePL' && shipment.quotes != null) {
      const acceptedQuote = shipment.quotes.find(({ status }) => {
        return status === 'Accepted'
      })
      if (acceptedQuote != null) {
        quote = acceptedQuote
      }
    }
    let schedule = shipment.schedule
    const drops = shipment.drops
    schedule = shipmentHelper.addQuotePortToSchedule({ schedule, quote, drops })
    shipment = { ...shipment, schedule }
    if (shipment.vehicle != null && shipment.vehicle != '') {
      switch (shipment.type) {
        case 'OnePL':
          const vehicleResponse = await vehicleService.getVehicle({ vehicleID: shipment.vehicle })
          return { ...shipment, vehicle: vehicleResponse }
          case 'TwoPL':
            const vehicleClassResponse = await vehicleService.getVehicleClass({ vehicleClassID: shipment.vehicle })
          return { ...shipment, vehicleClass: vehicleClassResponse }
        default:
          delete shipment['vehicle']
          return shipment
      }
    } else {
      return shipment
    }
  } catch (error) {
    return Promise.reject(error)
  }
}

async function getShipmentWithDropsForOptimization ({ shipmentID }) {
  try {
    const response = await appSync.query({
      query: GET_SHIPMENT_WITH_DROP_FOR_OPTIMIZATION,
      variables: {
        input: {
          id: shipmentID
        }
      },
      fetchPolicy: 'no-cache'
    })
    let shipment = response.data.getShipment.shipment
    if (shipment.vehicle != null) {
      switch (shipment.type) {
        case 'OnePL':
          const vehicleResponse = await vehicleService.getVehicle({ vehicleID: shipment.vehicle })
          return { ...shipment, vehicle: vehicleResponse }
          case 'TwoPL':
            const vehicleClassResponse = await vehicleService.getVehicleClass({ vehicleClassID: shipment.vehicle })
          return { ...shipment, vehicle: vehicleClassResponse }
        default:
          delete shipment['vehicle']
          return shipment
      }
    } else {
      return shipment
    }
  } catch (error) {
    return Promise.reject(error)
  }
}
// Shipment Mutations

const CREATE_SHIPMENT = gql`mutation createShipment($input: CreateShipmentInput!) {
  createShipment(input: $input) {
    id
    err
  }
}`
const UPDATE_SHIPMENT_LABELS = gql`mutation updateShipmentLabels($input: UpdateShipmentLabelsInput!) {
  updateShipmentLabels(input: $input) {
    err
  }
}`

const SET_GOODS_VALUE = gql`mutation setGoodsValue($input: SetGoodsValueInput!) {
  setGoodsValue(input: $input) {
    err
  }
}`

async function createShipment ({ labels }) {
  try {
    const response = await appSync.mutate({
      mutation: CREATE_SHIPMENT,
      variables: {
        input: {
          labels: labels,
          // departure: moment(departureDate).format()
        }
      },
      fetchPolicy: 'no-cache'
    })
    return response.data.createShipment
  } catch (error) {
    return Promise.reject(error)
  }
}
async function updateShipmentLabels ({ shipmentID, added, removed,labels }) {
  try {
    const response = await appSync.mutate({
      mutation: UPDATE_SHIPMENT_LABELS,
      variables: {
        input: {
          id: shipmentID,
          labels
        }
      },
      fetchPolicy: 'no-cache'
    })
    return response
  } catch (error) {
    return Promise.reject(error)
  }
}

async function setGoodsValue ({ id, value }) {
  try {
    const response = await appSync.mutate({
      mutation: SET_GOODS_VALUE,
      variables: {
        input: {
          id,
          value
        }
      },
      fetchPolicy: 'no-cache',
      addTypename: false
    })
    return response
  } catch (error) {
    return Promise.reject(error)
  }
}
